import { createGlobalStyle, css } from 'styled-components';

const fontImport = css`
  @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&family=Poppins:wght@400;600;700&display=swap');
`;

const GlobalStyle = createGlobalStyle`
  ${fontImport}

  :root {
    color-scheme: light;
    /* Spacing tokens for consistent spacing */
    --spacing-xs: 4px;
    --spacing-sm: 8px;
    --spacing-md: 16px;
    --spacing-lg: 24px;
    --spacing-xl: 32px;
    --spacing-2xl: 48px;
    --spacing-3xl: 64px;
    --spacing-4xl: 96px;
  }

  html {
    font-size: 16px;
    direction: rtl;
    scroll-behavior: smooth;
    /* Better text rendering */
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
  }

  body {
    margin: 0;
    padding: 0;
    background: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.textDark};
    font-family: ${({ theme }) => theme.fonts.ar};
    font-size: 1rem;
    line-height: 1.6;
    min-height: 100vh;
    /* Better spacing for body content */
    word-spacing: 0.05em;
    letter-spacing: 0.01em;
  }

  *, *::before, *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  /* Improved spacing for headings */
  h1, h2, h3, h4, h5, h6 {
    margin: 0;
    margin-bottom: ${({ theme }) => theme.layout.radiusSm};
    padding: 0;
    line-height: 1.3;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.textDark};
  }

  h1 {
    margin-bottom: ${({ theme }) => theme.layout.radiusMd};
  }

  h2 {
    margin-bottom: ${({ theme }) => theme.layout.radiusSm};
  }

  /* Improved paragraph spacing */
  p {
    margin: 0;
    margin-bottom: var(--spacing-md);
    line-height: 1.7;
    color: ${({ theme }) => theme.colors.textDark};
  }

  p:last-child {
    margin-bottom: 0;
  }

  /* Better list spacing */
  ul, ol {
    margin: 0;
    margin-bottom: var(--spacing-md);
    padding-inline-start: var(--spacing-xl);
    list-style-position: outside;
  }

  li {
    margin-bottom: var(--spacing-sm);
    line-height: 1.6;
  }

  li:last-child {
    margin-bottom: 0;
  }

  /* Improved image spacing */
  img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 0;
    /* Better image rendering */
    image-rendering: -webkit-optimize-contrast;
    image-rendering: crisp-edges;
  }

  /* Better link spacing and interaction */
  a {
    text-decoration: none;
    color: inherit;
    transition: color 0.2s ease, opacity 0.2s ease;
    display: inline-block;
  }

  a:hover {
    opacity: 0.85;
  }

  a:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.secondary};
    outline-offset: 2px;
    border-radius: 4px;
  }

  /* Improved button spacing */
  button {
    margin: 0;
    padding: 0;
    border: none;
    background: none;
    font-family: inherit;
    font-size: inherit;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  button:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.secondary};
    outline-offset: 2px;
    border-radius: 4px;
  }

  /* Better form element spacing */
  input, textarea, select {
    margin: 0;
    padding: var(--spacing-sm) var(--spacing-md);
    font-family: inherit;
    font-size: inherit;
    line-height: 1.5;
    border-radius: ${({ theme }) => theme.layout.radiusSm};
    transition: all 0.2s ease;
  }

  input:focus, textarea:focus, select:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }

  /* Improved selection styling */
  ::selection {
    background: ${({ theme }) => theme.colors.secondary};
    color: ${({ theme }) => theme.colors.textLight};
    text-shadow: none;
  }

  ::-moz-selection {
    background: ${({ theme }) => theme.colors.secondary};
    color: ${({ theme }) => theme.colors.textLight};
    text-shadow: none;
  }

  /* Better scrollbar styling */
  ::-webkit-scrollbar {
    width: 12px;
    height: 12px;
  }

  ::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.backgroundAlt};
  }

  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.divider};
    border-radius: 6px;
    border: 2px solid ${({ theme }) => theme.colors.backgroundAlt};
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.muted};
  }

  /* Improved spacing for RTL layout */
  [dir="rtl"] {
    text-align: right;
  }

  [dir="ltr"] {
    text-align: left;
  }

  /* Better spacing for sections */
  section {
    margin: 0;
    padding: var(--spacing-3xl) var(--spacing-lg);
  }

  @media (max-width: 768px) {
    section {
      padding: var(--spacing-2xl) var(--spacing-md);
    }
  }

  /* Improved spacing for containers */
  .container {
    width: 100%;
    max-width: ${({ theme }) => theme.layout.maxWidth};
    margin: 0 auto;
    padding: 0 var(--spacing-lg);
  }

  @media (max-width: 768px) {
    .container {
      padding: 0 var(--spacing-md);
    }
  }

  /* Better spacing utilities */
  .mb-0 { margin-bottom: 0 !important; }
  .mb-sm { margin-bottom: var(--spacing-sm) !important; }
  .mb-md { margin-bottom: var(--spacing-md) !important; }
  .mb-lg { margin-bottom: var(--spacing-lg) !important; }
  .mb-xl { margin-bottom: var(--spacing-xl) !important; }

  .mt-0 { margin-top: 0 !important; }
  .mt-sm { margin-top: var(--spacing-sm) !important; }
  .mt-md { margin-top: var(--spacing-md) !important; }
  .mt-lg { margin-top: var(--spacing-lg) !important; }
  .mt-xl { margin-top: var(--spacing-xl) !important; }

  .p-0 { padding: 0 !important; }
  .p-sm { padding: var(--spacing-sm) !important; }
  .p-md { padding: var(--spacing-md) !important; }
  .p-lg { padding: var(--spacing-lg) !important; }
  .p-xl { padding: var(--spacing-xl) !important; }

  /* Improved focus states for accessibility */
  *:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }

  /* Better spacing for interactive elements */
  button, a, input, textarea, select {
    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.colors.secondary};
      outline-offset: 2px;
      border-radius: ${({ theme }) => theme.layout.radiusSm};
    }
  }

  /* Smooth transitions for all interactive elements */
  button, a, input, textarea, select, img {
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }
`;

export default GlobalStyle;
